import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useLang } from "@/i18n";
import { useCart } from "@/context/CartContext";
import { useWishlist } from "@/context/WishlistContext";
import { useAuth } from "@/context/AuthContext";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { CurrencySwitcher } from "@/components/CurrencySwitcher";
import { ShoppingBag, User, Menu, X, Heart } from "lucide-react";

export const Header = () => {
  const { t } = useLang();
  const { count, setOpen } = useCart();
  const { count: wishCount } = useWishlist();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const navLinks = [
    { to: "/shop/rings", label: t("nav_rings") },
    { to: "/shop/necklaces", label: t("nav_necklaces") },
    { to: "/shop/earrings", label: t("nav_earrings") },
    { to: "/shop/bracelets", label: t("nav_bracelets") },
    { to: "/gifts", label: t("nav_gifts") },
    { to: "/shop", label: t("nav_shop") },
  ];

  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/70 border-b border-[#EAE6E1]/60">
      <div className="px-4 sm:px-8 lg:px-16">
        <div className="flex items-center justify-between h-20">
          <button
            data-testid="mobile-menu-toggle"
            className="lg:hidden text-[#1A1F2C]"
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>

          <Link to="/" data-testid="brand-logo" className="flex flex-col items-center lg:items-start">
            <span className="font-serif-display text-2xl sm:text-3xl leading-none text-[#1A1F2C]">Mimi Silver</span>
            <span className="text-[9px] tracking-luxe uppercase text-[#C5A880] mt-0.5">925 · {t("brand_tagline")}</span>
          </Link>

          <nav className="hidden lg:flex items-center gap-9">
            {navLinks.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.to.replace(/\//g, "-")}`}
                className="text-xs tracking-luxe uppercase text-[#1A1F2C] hover:text-[#C5A880] transition-colors duration-300"
              >
                {l.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-5">
            <CurrencySwitcher />
            <LanguageSwitcher />
            <button
              data-testid="account-button"
              onClick={() => navigate(user ? (user.role === "admin" ? "/admin" : "/account") : "/login")}
              className="text-[#1A1F2C] hover:text-[#C5A880] transition-colors duration-300"
            >
              <User className="w-5 h-5" strokeWidth={1.4} />
            </button>
            <button
              data-testid="wishlist-button"
              onClick={() => navigate("/wishlist")}
              className="relative text-[#1A1F2C] hover:text-[#C5A880] transition-colors duration-300"
            >
              <Heart className="w-5 h-5" strokeWidth={1.4} />
              {wishCount > 0 && (
                <span data-testid="wishlist-count" className="absolute -top-2 -right-2 bg-[#C5A880] text-white text-[10px] w-4 h-4 flex items-center justify-center">
                  {wishCount}
                </span>
              )}
            </button>
            <button
              data-testid="cart-button"
              onClick={() => setOpen(true)}
              className="relative text-[#1A1F2C] hover:text-[#C5A880] transition-colors duration-300"
            >
              <ShoppingBag className="w-5 h-5" strokeWidth={1.4} />
              {count > 0 && (
                <span
                  data-testid="cart-count"
                  className="absolute -top-2 -right-2 bg-[#C5A880] text-white text-[10px] w-4 h-4 flex items-center justify-center"
                >
                  {count}
                </span>
              )}
            </button>
          </div>
        </div>

        {mobileOpen && (
          <nav className="lg:hidden flex flex-col gap-4 pb-6 border-t border-[#EAE6E1] pt-4">
            {navLinks.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                onClick={() => setMobileOpen(false)}
                data-testid={`mobile-nav-link-${l.to.replace(/\//g, "-")}`}
                className="text-xs tracking-luxe uppercase text-[#1A1F2C]"
              >
                {l.label}
              </Link>
            ))}
          </nav>
        )}
      </div>
    </header>
  );
};
